import { useEffect, useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { Diamond, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import PasswordInput from "../components/PasswordInput";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Surface inactive-account banner when interceptor redirects here.
  useEffect(() => {
    if (searchParams.get("reason") === "inactive") {
      setError("Your account has been deactivated. Contact an administrator to regain access.");
      // Strip the query so a manual refresh doesn't keep the banner forever.
      searchParams.delete("reason");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A]">
      {/* Left: hero image */}
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1573408301185-9146fe634ad0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBkaWFtb25kJTIwamV3ZWxyeSUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzc4NzczMTUzfDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative h-full flex flex-col justify-end p-16">
          <div className="overline text-[0.7rem]">Premium Atelier System</div>
          <h2 className="serif text-5xl mt-4 mb-6 leading-tight">
            The atelier <br /> of fine stones.
          </h2>
          <p className="text-[#8A8A8A] text-sm max-w-md leading-relaxed">
            An internal command surface for inventory, accounting, and auction
            operations across our online and showroom inventories.
          </p>
          <div className="mt-12 hairline" />
          <div className="mt-6 mono text-xs text-[#8A8A8A]">OPIK JEWELS · CONFIDENTIAL ACCESS</div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md fade-up">
          <div className="flex items-center justify-center mb-10">
            <img
              src="/brand/opikjewels-logo-v2.png"
              alt="Opik Jewels"
              className="h-[180px] w-auto object-contain"
              data-testid="login-brand-logo"
            />
          </div>

          <div className="overline mb-3">Authentication</div>
          <h1 className="serif text-4xl mb-2">Sign in</h1>
          <p className="text-sm text-[#8A8A8A] mb-10">
            Access restricted to authorised members of the house.
          </p>

          <form onSubmit={onSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="luxe-input w-full px-4 py-3 text-sm"
                required
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <PasswordInput
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="luxe-input w-full px-4 py-3 text-sm"
                required
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-xs text-red-400 border border-red-900/40 px-4 py-3 bg-red-900/10"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={submitting}
              className="btn-gold w-full py-3.5 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Verifying" : "Enter"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
